import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowAltCircleDown, faArrowAltCircleUp } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { useRecoilValue, useRecoilState } from 'recoil';
import {
  Table,
  Select,
  Input,
  Spin
} from 'antd';
import { notificationsAtom, userPrivilagesAtom } from '../../../atoms/Atoms';
import MainPage from '../../shared-components/main-page/MainPage';
import { H1 } from '../../shared-components/typography/Title';
import api from '../../../api/api';
import { thousand, } from '../../../functions';

function MergedSalesDailySummary() {
  const { t } = useTranslation();
  const { Option } = Select;
  const sortFilters = [
    { name: `${t('name')} Z-A`, type: 0 },
    { name: `${t('name')} A-Z`, type: 1 },
    { name: `${t('category')} Z-A`, type: 2 },
    { name: `${t('category')} A-Z`, type: 3 },
    { name: `${t('today_sales')} ${t('desc')}`, type: 24 },
    { name: `${t('today_sales')} ${t('asc')}`, type: 25 },
    { name: `${t('yesterday_sales')} ${t('desc')}`, type: 22 },
    { name: `${t('yesterday_sales')} ${t('asc')}`, type: 23 },
    { name: `${t('two_days_ago_sales')} ${t('desc')}`, type: 20 },
    { name: `${t('two_days_ago_sales')} ${t('asc')}`, type: 21 },
    { name: `${t('three_days_ago_sales')} ${t('desc')}`, type: 18 },
    { name: `${t('three_days_ago_sales')} ${t('asc')}`, type: 19 },
    { name: `${t('four_days_ago_sales')} ${t('desc')}`, type: 16 },
    { name: `${t('four_days_ago_sales')} ${t('asc')}`, type: 17 },
    { name: `${t('five_days_ago_sales')} ${t('desc')}`, type: 14 },
    { name: `${t('five_days_ago_sales')} ${t('asc')}`, type: 15 },
    { name: `${t('six_days_ago_sales')} ${t('desc')}`, type: 12 },
    { name: `${t('six_days_ago_sales')} ${t('asc')}`, type: 13 },
    { name: `${t('seven_days_ago_sales')} ${t('desc')}`, type: 10 },
    { name: `${t('seven_days_ago_sales')} ${t('asc')}`, type: 11 },
    { name: `${t('eight_days_ago_sales')} ${t('desc')}`, type: 8 },
    { name: `${t('eight_days_ago_sales')} ${t('asc')}`, type: 9 },
    { name: `${t('nine_days_ago_sales')} ${t('desc')}`, type: 6 },
    { name: `${t('nine_days_ago_sales')} ${t('asc')}`, type: 7 },
    { name: `${t('ten_days_ago_sales')} ${t('desc')}`, type: 4 },
    { name: `${t('ten_days_ago_sales')} ${t('asc')}`, type: 5 },
  ];
  const [notifications, setNotifications] = useRecoilState(notificationsAtom);
  const userPrivilages = useRecoilValue(userPrivilagesAtom);
  const [loading, setLoading] = useState(false);
  const [lastSearchTerm, setLastSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [categories, setCategories] = useState([]);
  const [searchResult, setSearchResult] = useState(null);
  const [selectedSortFilter, setSelectedSortFilter] = useState(4);
  const [selectedCategory, setSelectedCategory] = useState(null);

  function getCategories() {
    setLoadingCategories(true);
    api.get('window/summaries/sales/daily/cateogries')
      .then((response) => {
        setLoadingCategories(false);
        if (response.data.success) {
          setCategories(response.data.data);
          if (selectedCategory == null) {
            setSelectedCategory(_.filter(response.data.data, (c) => c)[0]);
          }
        } else {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response.data.error,
              error: true,
            }
          ]);
        }
      })
      .catch((error) => {
        setLoadingCategories(false);
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          }
        ]);
      })
  }

  function search(value, page) {
    if (selectedCategory == null) {
      return;
    }
    setLastSearchTerm(value);
    setCurrentPage(page);
    setLoading(true);
    api
      .post(
        'window/summaries/sales/daily/search',
        {
          Query: value,
          Size: pageSize,
          Page: page,
          Sort: selectedSortFilter,
          Category: selectedCategory
        }
      )
      .then((response) => {
        setLoading(false);
        if (response.data.success) {
          setSearchResult(response.data.data);
        } else {
          setSearchResult({
            ...searchResult,
            items: []
          });
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response.data.error,
              error: true,
            },
          ]);
        }
      })
      .catch((error) => {
        setLoading(false);
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          },
        ]);
      });
  }

  function onSearch(value) {
    search(value, 0);
  }

  const onTableChange = (page, size) => {
    if (page - 1 !== currentPage) {
      setCurrentPage(page - 1);
      search(lastSearchTerm, page - 1);
    } else {
      setPageSize(size);
    }
  };

  useEffect(() => {
    search(lastSearchTerm, 0);
  }, [selectedSortFilter, selectedCategory, pageSize]);

  useEffect(() => {
    setSelectedSortFilter(4);
    getCategories();
  }, []);

  return (
    <MainPage>
      <div className="flex mt-4">
        <H1 text={t('sales_daily_summary_window')} />
      </div>
      <div className="lg:flex items-center justify-center">
        <Input.Search
          placeholder={`${t('search')}...`}
          allowClear
          disabled={loadingCategories}
          className="mt-4"
          enterButton={t('search')}
          size="large"
          onClear={() => onSearch('')}
          onSearch={(val) => onSearch(val)}
        />
        <div className="grid grid-cols-2 md:grid-cols-4 md:space-x-2 lg:flex">
          <Select
            loading={loadingCategories}
            className="h-10 col-span-1 mt-2 lg:mt-4 lg:w-48 mr-1 md:mr-0 ml-0 lg:ml-2"
            value={selectedCategory}
            disabled={loadingCategories}
            onChange={(value) => setSelectedCategory(value)}
          >
            {/* <Option
              key="nocategory"
              value=""
              className="text-sm flex items-center text-start"
            >
              {t('any_category')}
            </Option> */}
            {_.map(categories, (category) => (
              <Option
                key={category ?? '[no_data]'}
                value={category ?? '[no_data]'}
                className="text-sm flex items-center text-start"
              >
                {category ?? t('[no_data]')}
              </Option>
            ))}
          </Select>
          <Select
            className="h-10 col-span-1 mt-2 lg:mt-4 lg:w-48 ml-1 md:ml-0"
            value={selectedSortFilter}
            disabled={loadingCategories}
            onChange={(value) => setSelectedSortFilter(value)}
          >
            {_.map(sortFilters, (filter) => (
              <Option
                key={filter.type}
                value={filter.type}
                className="text-sm flex items-center text-start"
              >
                {filter.name}
              </Option>
            ))}
          </Select>
        </div>
      </div>
      <div className="mt-4">{t('found_in_search')}: {searchResult?.items ? thousand(searchResult?.total) : 0}</div>
      <Table
        locale={{
          emptyText: t('no_data')
        }}
        bordered
        loading={loading}
        rowKey="itemCode"
        pagination={{
          pageSize,
          showSizeChanger: true,
          current: currentPage + 1,
          total: searchResult?.total,
          onChange: onTableChange
        }}
        dataSource={searchResult?.items}
        columns={[
          {
            title: t('code'),
            key: 'itemCode',
            align: 'left',
            className: 'text-sm font-semibold text-gray-800',
            render: (row) => (
              <div className="flex space-x-2"><span>{row.itemCode}</span></div>
            ),
          },
          {
            title: t('product'),
            key: 'name',
            align: 'left',
            className: 'text-sm',
            render: (row) => (
              <span>{row.name}</span>
            ),
          },
          {
            title: t('ten_days_ago'),
            key: 'totalSalesTenDaysAgo',
            align: 'left',
            className: 'text-sm',
            render: (row) => (
              <span className="lowercase">{thousand(row.totalSalesTenDaysAgo)}</span>
            ),
          },
          {
            title: t('nine_days_ago'),
            key: 'totalSalesNineDaysAgo',
            align: 'left',
            className: 'text-sm',
            render: (row) => (
              <div>
                <span>{row.totalSalesNineDaysAgo > row.totalSalesTenDaysAgo ? <FontAwesomeIcon className="text-green-400" icon={faArrowAltCircleUp} /> : <FontAwesomeIcon className="text-red-400" icon={faArrowAltCircleDown} />}</span>
                <span className="lowercase"> {thousand(row.totalSalesNineDaysAgo)}</span>
              </div>
            ),
          },
          {
            title: t('eight_days_ago'),
            key: 'totalSalesEightDaysAgo',
            align: 'left',
            className: 'text-sm',
            render: (row) => (
              <div>
                <span>{row.totalSalesEightDaysAgo > row.totalSalesNineDaysAgo ? <FontAwesomeIcon className="text-green-400" icon={faArrowAltCircleUp} /> : <FontAwesomeIcon className="text-red-400" icon={faArrowAltCircleDown} />}</span>
                <span className="lowercase"> {thousand(row.totalSalesEightDaysAgo)}</span>
              </div>
            ),
          },
          {
            title: t('seven_days_ago'),
            key: 'totalSalesSevenDaysAgo',
            align: 'left',
            className: 'text-sm',
            render: (row) => (
              <div>
                <span>{row.totalSalesSevenDaysAgo > row.totalSalesEightDaysAgo ? <FontAwesomeIcon className="text-green-400" icon={faArrowAltCircleUp} /> : <FontAwesomeIcon className="text-red-400" icon={faArrowAltCircleDown} />}</span>
                <span className="lowercase"> {thousand(row.totalSalesSevenDaysAgo)}</span>
              </div>
            ),
          },
          {
            title: t('six_days_ago'),
            key: 'totalSalesSixDaysAgo',
            align: 'left',
            className: 'text-sm',
            render: (row) => (
              <div>
                <span>{row.totalSalesSixDaysAgo > row.totalSalesSevenDaysAgo ? <FontAwesomeIcon className="text-green-400" icon={faArrowAltCircleUp} /> : <FontAwesomeIcon className="text-red-400" icon={faArrowAltCircleDown} />}</span>
                <span className="lowercase"> {thousand(row.totalSalesSixDaysAgo)}</span>
              </div>
            ),
          },
          {
            title: t('five_days_ago'),
            key: 'totalSalesFiveDaysAgo',
            align: 'left',
            className: 'text-sm',
            render: (row) => (
              <div>
                <span>{row.totalSalesFiveDaysAgo > row.totalSalesSixDaysAgo ? <FontAwesomeIcon className="text-green-400" icon={faArrowAltCircleUp} /> : <FontAwesomeIcon className="text-red-400" icon={faArrowAltCircleDown} />}</span>
                <span className="lowercase"> {thousand(row.totalSalesFiveDaysAgo)}</span>
              </div>
            ),
          },
          {
            title: t('four_days_ago'),
            key: 'totalSalesFourDaysAgo',
            align: 'left',
            className: 'text-sm',
            render: (row) => (
              <div>
                <span>{row.totalSalesFourDaysAgo > row.totalSalesFiveDaysAgo ? <FontAwesomeIcon className="text-green-400" icon={faArrowAltCircleUp} /> : <FontAwesomeIcon className="text-red-400" icon={faArrowAltCircleDown} />}</span>
                <span className="lowercase"> {thousand(row.totalSalesFourDaysAgo)}</span>
              </div>
            ),
          },
          {
            title: t('three_days_ago'),
            key: 'totalSalesThreeDaysAgo',
            align: 'left',
            className: 'text-sm',
            render: (row) => (
              <div>
                <span>{row.totalSalesThreeDaysAgo > row.totalSalesFourDaysAgo ? <FontAwesomeIcon className="text-green-400" icon={faArrowAltCircleUp} /> : <FontAwesomeIcon className="text-red-400" icon={faArrowAltCircleDown} />}</span>
                <span className="lowercase"> {thousand(row.totalSalesThreeDaysAgo)}</span>
              </div>
            ),
          },
          {
            title: t('two_days_ago'),
            key: 'totalSalesTwoDaysAgo',
            align: 'left',
            className: 'text-sm',
            render: (row) => (
              <div>
                <span>{row.totalSalesTwoDaysAgo > row.totalSalesThreeDaysAgo ? <FontAwesomeIcon className="text-green-400" icon={faArrowAltCircleUp} /> : <FontAwesomeIcon className="text-red-400" icon={faArrowAltCircleDown} />}</span>
                <span className="lowercase"> {thousand(row.totalSalesTwoDaysAgo)}</span>
              </div>
            ),
          },
          {
            title: t('one_day_ago'),
            key: 'totalSalesOneDayAgo',
            align: 'left',
            className: 'text-sm',
            render: (row) => (
              <div>
                <span>{row.totalSalesOneDayAgo > row.totalSalesTwoDaysAgo ? <FontAwesomeIcon className="text-green-400" icon={faArrowAltCircleUp} /> : <FontAwesomeIcon className="text-red-400" icon={faArrowAltCircleDown} />}</span>
                <span className="lowercase"> {thousand(row.totalSalesOneDayAgo)}</span>
              </div>
            ),
          },
          {
            title: t('today'),
            key: 'totalSalesToday',
            align: 'left',
            className: 'text-sm',
            render: (row) => (
              <div>
                <span>{thousand(row.totalSalesToday)}</span>
              </div>
            ),
          }
        ]}
        expandable={{
          expandRowByClick: false,
          rowExpandable: (record) => true,
          onExpand: (expanded, record) => {
            if (expanded && !record.loadedStores && !record.loadingStores) {
              const temp = { ...searchResult };
              for (let i = 0; i < temp.items.length; i++) {
                if (temp.items[i].itemCode === record.itemCode) {
                  temp.items[i].loadingStores = true;
                }
              }
              setSearchResult(temp);
              api.get(`window/summaries/sales/daily/bystore?itemCode=${record.itemCode}`)
                .then((response) => {
                  const temp2 = { ...searchResult };
                  if (response.data.success) {
                    for (let i = 0; i < temp2.items.length; i++) {
                      if (temp2.items[i].itemCode === record.itemCode) {
                        temp2.items[i].stores = response.data.data;
                      }
                    }  
                  } else {
                    setNotifications([
                      ...notifications,
                      {
                        title: t('error'),
                        description: response.data.error,
                        error: true,
                      },
                    ]);
                  }
                  for (let i = 0; i < temp2.items.length; i++) {
                    if (temp2.items[i].itemCode === record.itemCode) {
                      temp2.items[i].loadingStores = false;
                    }
                  }
                  console.log(temp2);
                  setSearchResult(temp2);
                })
                .catch((error) => {
                  const temp2 = { ...searchResult };
                  for (let i = 0; i < temp2.items.length; i++) {
                    if (temp2.items[i].itemCode === record.itemCode) {
                      temp2.items[i].loadingStores = false;
                    }
                  }
                  setSearchResult(temp2);
                  setNotifications([
                    ...notifications,
                    {
                      title: t('error'),
                      description: error.message,
                      error: true,
                    },
                  ]);
                });
            }
          },
          expandedRowRender: (record) => (
            <Spin spinning={false}>
              <div className="w-full mb-8">
                <Table
                  loading={record.loadingStores}
                  dataSource={record?.stores}
                  columns={[
                    {
                      title: t('source'),
                      key: 'sourceSystem',
                      align: 'left',
                      className: 'text-sm font-semibold text-gray-800',
                      render: (row) => (
                        <div className="flex space-x-2"><span>{row.sourceSystem}</span></div>
                      ),
                    },
                    {
                      title: t('store'),
                      key: 'store',
                      align: 'left',
                      className: 'text-sm font-semibold text-gray-800',
                      render: (row) => (
                        <div className="flex space-x-2"><span>{row.storeName}</span></div>
                      ),
                    },
                    {
                      title: t('code'),
                      key: 'itemCode',
                      align: 'left',
                      className: 'text-sm font-semibold text-gray-800',
                      render: (row) => (
                        <div className="flex space-x-2"><span>{row.itemCode}</span></div>
                      ),
                    },
                    {
                      title: t('product'),
                      key: 'name',
                      align: 'left',
                      className: 'text-sm',
                      render: (row) => (
                        <span>{row.name}</span>
                      ),
                    },
                    {
                      title: t('ten_days_ago'),
                      key: 'totalSalesTenDaysAgo',
                      align: 'left',
                      className: 'text-sm',
                      render: (row) => (
                        <span className="lowercase">{thousand(row.salesTenDaysAgo)}</span>
                      ),
                    },
                    {
                      title: t('nine_days_ago'),
                      key: 'totalSalesNineDaysAgo',
                      align: 'left',
                      className: 'text-sm',
                      render: (row) => (
                        <div>
                          <span>{row.salesNineDaysAgo > row.salesTenDaysAgo ? <FontAwesomeIcon className="text-green-400" icon={faArrowAltCircleUp} /> : <FontAwesomeIcon className="text-red-400" icon={faArrowAltCircleDown} />}</span>
                          <span className="lowercase"> {thousand(row.salesNineDaysAgo)}</span>
                        </div>
                      ),
                    },
                    {
                      title: t('eight_days_ago'),
                      key: 'totalSalesEightDaysAgo',
                      align: 'left',
                      className: 'text-sm',
                      render: (row) => (
                        <div>
                          <span>{row.salesEightDaysAgo > row.salesNineDaysAgo ? <FontAwesomeIcon className="text-green-400" icon={faArrowAltCircleUp} /> : <FontAwesomeIcon className="text-red-400" icon={faArrowAltCircleDown} />}</span>
                          <span className="lowercase"> {thousand(row.salesEightDaysAgo)}</span>
                        </div>
                      ),
                    },
                    {
                      title: t('seven_days_ago'),
                      key: 'totalSalesSevenDaysAgo',
                      align: 'left',
                      className: 'text-sm',
                      render: (row) => (
                        <div>
                          <span>{row.salesSevenDaysAgo > row.salesEightDaysAgo ? <FontAwesomeIcon className="text-green-400" icon={faArrowAltCircleUp} /> : <FontAwesomeIcon className="text-red-400" icon={faArrowAltCircleDown} />}</span>
                          <span className="lowercase"> {thousand(row.salesSevenDaysAgo)}</span>
                        </div>
                      ),
                    },
                    {
                      title: t('six_days_ago'),
                      key: 'totalSalesSixDaysAgo',
                      align: 'left',
                      className: 'text-sm',
                      render: (row) => (
                        <div>
                          <span>{row.salesSixDaysAgo > row.salesSevenDaysAgo ? <FontAwesomeIcon className="text-green-400" icon={faArrowAltCircleUp} /> : <FontAwesomeIcon className="text-red-400" icon={faArrowAltCircleDown} />}</span>
                          <span className="lowercase"> {thousand(row.salesSixDaysAgo)}</span>
                        </div>
                      ),
                    },
                    {
                      title: t('five_days_ago'),
                      key: 'totalSalesFiveDaysAgo',
                      align: 'left',
                      className: 'text-sm',
                      render: (row) => (
                        <div>
                          <span>{row.salesFiveDaysAgo > row.salesSixDaysAgo ? <FontAwesomeIcon className="text-green-400" icon={faArrowAltCircleUp} /> : <FontAwesomeIcon className="text-red-400" icon={faArrowAltCircleDown} />}</span>
                          <span className="lowercase"> {thousand(row.salesFiveDaysAgo)}</span>
                        </div>
                      ),
                    },
                    {
                      title: t('four_days_ago'),
                      key: 'totalSalesFourDaysAgo',
                      align: 'left',
                      className: 'text-sm',
                      render: (row) => (
                        <div>
                          <span>{row.salesFourDaysAgo > row.salesFiveDaysAgo ? <FontAwesomeIcon className="text-green-400" icon={faArrowAltCircleUp} /> : <FontAwesomeIcon className="text-red-400" icon={faArrowAltCircleDown} />}</span>
                          <span className="lowercase"> {thousand(row.salesFourDaysAgo)}</span>
                        </div>
                      ),
                    },
                    {
                      title: t('three_days_ago'),
                      key: 'totalSalesThreeDaysAgo',
                      align: 'left',
                      className: 'text-sm',
                      render: (row) => (
                        <div>
                          <span>{row.salesThreeDaysAgo > row.salesFourDaysAgo ? <FontAwesomeIcon className="text-green-400" icon={faArrowAltCircleUp} /> : <FontAwesomeIcon className="text-red-400" icon={faArrowAltCircleDown} />}</span>
                          <span className="lowercase"> {thousand(row.salesThreeDaysAgo)}</span>
                        </div>
                      ),
                    },
                    {
                      title: t('two_days_ago'),
                      key: 'totalSalesTwoDaysAgo',
                      align: 'left',
                      className: 'text-sm',
                      render: (row) => (
                        <div>
                          <span>{row.salesTwoDaysAgo > row.salesThreeDaysAgo ? <FontAwesomeIcon className="text-green-400" icon={faArrowAltCircleUp} /> : <FontAwesomeIcon className="text-red-400" icon={faArrowAltCircleDown} />}</span>
                          <span className="lowercase"> {thousand(row.salesTwoDaysAgo)}</span>
                        </div>
                      ),
                    },
                    {
                      title: t('one_day_ago'),
                      key: 'totalSalesOneDayAgo',
                      align: 'left',
                      className: 'text-sm',
                      render: (row) => (
                        <div>
                          <span>{row.salesOneDayAgo > row.salesTwoDaysAgo ? <FontAwesomeIcon className="text-green-400" icon={faArrowAltCircleUp} /> : <FontAwesomeIcon className="text-red-400" icon={faArrowAltCircleDown} />}</span>
                          <span className="lowercase"> {thousand(row.salesOneDayAgo)}</span>
                        </div>
                      ),
                    },
                    {
                      title: t('today'),
                      key: 'totalSalesToday',
                      align: 'left',
                      className: 'text-sm',
                      render: (row) => (
                        <div>
                          <span>{thousand(row.salesToday)}</span>
                        </div>
                      ),
                    }
                  ]}
                />
              </div>
            </Spin>
          )
        }}
      />
    </MainPage>
  );
}

export default MergedSalesDailySummary;
